import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  Bell,
  Building2,
  Check,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  LogOut,
  Menu,
  Moon,
  Palette as PaletteIcon,
  Search,
  Sun,
  UserRound,
} from 'lucide-react';
import { hasPermission, type Portal } from '@kts/shared';
import { useAuth } from '@kts/client';
import { Trans, useT } from '@kts/i18n';
import {
  Avatar,
  Dropdown,
  IconButton,
  LanguageSwitcher,
  LogoMark,
  PALETTES,
  PALETTE_LABELS,
  Toaster,
  toast,
  useTheme,
  type Palette,
} from '@kts/ui';
import { api } from '@/lib/api';
import { errorMessage, useGet } from '@/lib/hooks';
import { useAppLocale } from '@/lib/locale';
import { navFor, portalHome, type NavGroup } from '../nav';
import { CommandPalette } from './CommandPalette';
import { NotificationsMenu } from './NotificationsMenu';

function useVisibleNav(portal: Portal): NavGroup[] {
  const perms = useAuth((s) => s.perms);
  const isSuper = useAuth((s) => s.me?.isSuper ?? false);
  return useMemo(
    () =>
      navFor(portal)
        .map((g) => ({
          ...g,
          items: g.items.filter((i) => !i.perms || i.perms.some((p) => hasPermission(perms, p, isSuper))),
        }))
        .filter((g) => g.items.length > 0),
    [portal, perms, isSuper],
  );
}

export function Shell({ portal }: { portal: Portal }) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('kts.sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const loc = useLocation();
  const nav = useVisibleNav(portal);

  useEffect(() => setMobileOpen(false), [loc.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem('kts.sidebar', collapsed ? 'collapsed' : 'open');
    } catch {
      /* e'tiborsiz */
    }
  }, [collapsed]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="kts-shell" data-collapsed={collapsed} data-mobile-open={mobileOpen}>
      {mobileOpen && <div className="kts-sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
      <Sidebar portal={portal} nav={nav} collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      <main className="kts-main">
        <Topbar portal={portal} nav={nav} onMenu={() => setMobileOpen(true)} onSearch={() => setPaletteOpen(true)} />
        <div className="kts-content">
          <Outlet />
        </div>
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} nav={nav} />
      <ThemedToaster />
    </div>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="top-right" richColors closeButton />;
}

// ------------------------------------------------------------------ Sidebar
function Sidebar({ portal, nav, collapsed, onToggle }: { portal: Portal; nav: NavGroup[]; collapsed: boolean; onToggle: () => void }) {
  const me = useAuth((s) => s.me);
  const t = useT('app');
  return (
    <aside className="kts-sidebar">
      <div className="kts-sidebar__panel">
        <div className="kts-sidebar__brand">
          <LogoMark size={34} />
          <span className="kts-sidebar__brand-name">Keytosell</span>
          <span className="kts-sidebar__portal" title={portal === 'work' ? t('portal.workTitle') : t('portal.sellerTitle')}>
            {portal === 'work' ? 'WORK' : 'SELLER'}
          </span>
        </div>
        <OrgSwitcher collapsed={collapsed} />
        <nav className="kts-sidebar__nav" aria-label={t('shell.navAria')}>
          {nav.map((g) => (
            <div key={g.labelKey} className="kts-nav-group">
              <div className="kts-nav-group__label">{t(g.labelKey)}</div>
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className="kts-nav-item"
                  title={collapsed ? t(it.labelKey) : undefined}
                >
                  <it.icon />
                  <span>{t(it.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="kts-sidebar__footer">
          <button type="button" className="kts-nav-item" style={{ width: '100%', border: 'none', background: 'transparent' }} onClick={onToggle}>
            {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
            <span>{collapsed ? t('shell.expand') : t('shell.collapse')}</span>
          </button>
          {!collapsed && me && (
            <div style={{ padding: '6px 12px 2px', fontSize: 11, color: 'var(--muted)' }}>v0.1 · {me.org?.code}</div>
          )}
        </div>
      </div>
    </aside>
  );
}

function OrgSwitcher({ collapsed }: { collapsed: boolean }) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const t = useT('app');
  const tc = useT('common');
  if (!me) return null;
  /*
   * `me.org` bo'lmasa ham almashtirgich KO'RINADI. Aks holda tashkiloti topilmagan
   * foydalanuvchi uchun chiqish yo'li qolmaydi — aynan shu holat 2026-10-24 da bo'lgan.
   */
  const others = me.memberships.filter((m) => m.orgId !== me.org?.id);
  const switchTo = async (orgId: string) => {
    try {
      const next = await useAuth.getState().switchOrg(api, orgId);
      navigate(portalHome(next.portal), { replace: true });
      toast.success(t('shell.orgSwitched', { name: next.org?.name ?? '' }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const card = (
    <>
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--accent-soft)',
          color: 'var(--accent-strong)',
          flexShrink: 0,
        }}
      >
        <Building2 size={16} />
      </span>
      {!collapsed && (
        <span className="kts-sidebar__org-text" style={{ minWidth: 0, flex: 1 }}>
          {/* Tashkilot topilmasa ham blok ko'rinadi: pastda almashtirish ro'yxati bor. */}
          <div className="kts-sidebar__org-name">{me.org?.name ?? t('shell.orgUnknown')}</div>
          <div className="kts-sidebar__org-sub">
            {!me.org
              ? t('shell.orgUnknownSub')
              : me.org.kind === 'platform'
                ? t('shell.orgPlatform')
                : t('shell.orgPartner', { code: me.org.code })}
          </div>
        </span>
      )}
      {!collapsed && others.length > 0 && <ChevronsUpDown size={15} style={{ color: 'var(--muted)', flexShrink: 0 }} />}
    </>
  );
  if (others.length === 0) return <div className="kts-sidebar__org">{card}</div>;
  return (
    <Dropdown
      align="left"
      width={260}
      trigger={({ toggle }) => (
        <button type="button" className="kts-sidebar__org" onClick={toggle} aria-label={tc('auth.switchOrg')}>
          {card}
        </button>
      )}
      items={[
        { key: 'l', kind: 'label', label: tc('auth.switchOrg') },
        ...me.memberships.map((m) => ({
          key: m.orgId,
          label: (
            <span>
              {m.orgName}
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)' }}>
                {m.portal === 'work' ? t('portal.work') : t('portal.seller')}
              </span>
            </span>
          ),
          icon: m.orgId === me.org!.id ? <Check /> : <Building2 />,
          active: m.orgId === me.org!.id,
          onSelect: () => m.orgId !== me.org!.id && void switchTo(m.orgId),
        })),
      ]}
    />
  );
}

// ------------------------------------------------------------------ Topbar
function Topbar({ portal, nav, onMenu, onSearch }: { portal: Portal; nav: NavGroup[]; onMenu: () => void; onSearch: () => void }) {
  const me = useAuth((s) => s.me);
  const loc = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useT('app');
  const tc = useT('common');
  const { locale, setLocale } = useAppLocale();
  const unread = useGet<{ count: number }>('/notifications/unread-count', undefined, { refetchInterval: 30_000 });

  const home = portalHome(portal);
  const isHome = loc.pathname === home || loc.pathname === `${home}/`;
  const current = nav
    .flatMap((g) => g.items)
    .filter((i) => loc.pathname === i.to || loc.pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const firstName = me?.user.fullName.split(' ')[0] ?? '';

  const logout = async () => {
    await useAuth.getState().logout(api);
    navigate('/login', { replace: true });
  };

  return (
    <header className="kts-topbar">
      <div className="kts-topbar__inner">
        <IconButton label={t('shell.menu')} className="kts-mobile-only" onClick={onMenu}>
          <Menu />
        </IconButton>
        {isHome ? (
          <div className="kts-topbar__greet">
            <Trans t={t} i18nKey="shell.greeting" values={{ name: firstName }} components={{ em: <em /> }} />
          </div>
        ) : (
          <div className="kts-topbar__title">{current ? t(current.labelKey) : 'Keytosell'}</div>
        )}
        <button type="button" className="kts-topbar__search" onClick={onSearch}>
          <Search size={16} />
          <span>{t('shell.searchHint')}</span>
          <kbd>Ctrl K</kbd>
        </button>
        <div className="kts-topbar__actions">
          <LanguageSwitcher locale={locale} onChange={(l) => void setLocale(l)} />
          <IconButton label={theme.resolved === 'dark' ? t('theme.toLight') : t('theme.toDark')} onClick={theme.toggle}>
            {theme.resolved === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
          <Dropdown
            width={380}
            trigger={({ toggle }) => (
              <IconButton label={t('notifications.title')} dot={(unread.data?.count ?? 0) > 0} onClick={toggle}>
                <Bell />
              </IconButton>
            )}
          >
            {(close) => <NotificationsMenu onNavigate={close} />}
          </Dropdown>
          <Dropdown
            width={260}
            trigger={({ toggle }) => (
              <button type="button" className="kts-user-chip" onClick={toggle}>
                <Avatar name={me?.user.fullName ?? '?'} src={me?.user.avatarUrl} size={32} />
                <span className="kts-user-chip__text">
                  <div className="kts-user-chip__name">{me?.user.fullName}</div>
                  <div className="kts-user-chip__sub">{me?.roles[0]?.name ?? (me?.isSuper ? t('shell.superAdmin') : '')}</div>
                </span>
              </button>
            )}
          >
            {(close) => (
              <>
                <div style={{ padding: '10px 10px 8px' }}>
                  <div style={{ fontWeight: 700 }}>{me?.user.fullName}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{me?.user.phone ?? me?.user.login}</div>
                </div>
                <div className="kts-menu__sep" />
                <button
                  type="button"
                  className="kts-menu__item"
                  onClick={() => {
                    close();
                    navigate(`${home}/profile`);
                  }}
                >
                  <UserRound /> {tc('auth.profile')}
                </button>
                <div className="kts-menu__label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PaletteIcon size={12} /> {t('theme.paletteTitle')}
                </div>
                <div style={{ display: 'flex', gap: 8, padding: '4px 10px 10px', flexWrap: 'wrap' }}>
                  {PALETTES.map((p: Palette) => (
                    <button
                      key={p}
                      type="button"
                      title={t(`theme.palette.${p}`)}
                      aria-label={t(`theme.palette.${p}`)}
                      onClick={() => {
                        theme.setPalette(p);
                        void api.put('/me/prefs', { palette: p }).catch(() => undefined);
                      }}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: PALETTE_LABELS[p].swatch,
                        border: theme.palette === p ? '2px solid var(--text)' : '2px solid transparent',
                        boxShadow: '0 0 0 1px var(--line-strong)',
                      }}
                    />
                  ))}
                </div>
                <div className="kts-menu__sep" />
                <button
                  type="button"
                  className="kts-menu__item kts-menu__item--danger"
                  onClick={() => {
                    close();
                    void logout();
                  }}
                >
                  <LogOut /> {tc('auth.logout')}
                </button>
              </>
            )}
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
